/**
 * Application Routes Constants
 *
 * Centralized route definitions for the entire application.
 * These constants are used across different layers to maintain consistency
 * and avoid hardcoded route strings.
 */

// Core application routes
export const APP_ROUTES = {
  // Root routes
  ROOT: '/',
  DASHBOARD: '/dashboard',

  // Authentication routes
  AUTH: {
    BASE: '/auth',
    LOGIN: '/auth/login',
    CALLBACK: '/auth/callback',
    LOGOUT: '/auth/logout'
  },

  // Feature routes
  RESERVATIONS: {
    BASE: '/reservas',
    LIST: '/reservas',
    DETAIL: '/reservas/:productType/:id',
    CREATE: '/reservas/crear',
    EDIT: '/reservas/:productType/:id/editar'
  },

  FULFILLMENT: {
    QUEUE: '/reservas/gestion-proveedor'
  },

  SUPPLIER_PAYABLES: {
    LIST: '/reservas/pagos-proveedores'
  },

  // Back-office configuration routes
  SETTINGS: {
    BASE: '/settings',
    AGENCIES: '/settings/agencies',
    AGENCY_DETAIL: '/settings/agencies/:id'
  },

  // Error and status routes
  UNAUTHORIZED: '/unauthorized',
  NOT_FOUND: '/404',
  SERVER_ERROR: '/500'
} as const;

// Utility function to build dynamic routes
export const buildRoute = {
  reservationDetail: (productType: string, id: string) => `/reservas/${productType.toLowerCase()}/${id}`,
  reservationEdit: (productType: string, id: string) => `/reservas/${productType.toLowerCase()}/${id}/editar`,
  authWithReturn: (returnUrl?: string) =>
    returnUrl ? `/auth/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/auth/login',
  // Agency ids are opaque backend strings, not GUIDs (e.g. "club-miles-pe-u2RT6ThMfoyTJmYAZ"),
  // so they are encoded rather than interpolated raw.
  agencySettings: (id: string) => `/settings/agencies/${encodeURIComponent(id)}`
};

// Type exports for better type safety
export type AppRoute = typeof APP_ROUTES;
export type AuthRoute = typeof APP_ROUTES.AUTH;
export type ReservationRoute = typeof APP_ROUTES.RESERVATIONS;
export type SettingsRoute = typeof APP_ROUTES.SETTINGS;
export type FulfillmentRoute = typeof APP_ROUTES.FULFILLMENT;
